'use client'
import type { Dispatch, KeyboardEvent, RefObject, SetStateAction } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import queryString from 'query-string'
import { usePathname, useRouter } from 'next/navigation'
import { useUpdateEffect } from '@chakra-ui/react'
import { useDebouncedCallback } from 'ui'
import { useCustomerScopedRequest } from '../../common/hooks/useCustomerScope'
import type { StorefrontSearchRes } from '../types'

export interface UseSearchReturnType {
  data?: StorefrontSearchRes['content'] | null
  preservedData?: StorefrontSearchRes['content'] | null
  isLoading: boolean
  error?: string
  searchQuery: string
  clearSearch: () => void
  isSearchPage: boolean
  inputRef: RefObject<HTMLInputElement | null>
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>, callback?: () => void) => void
  submitAdvancedSearch: () => void
  advancedSearchQuery: string
  setAdvancedSearchQuery: Dispatch<SetStateAction<string>>
  searchPagePath: string
}

export interface UseSearchConfig {
  onSubmit?: (value: string) => void
  paths: {
    searchApi: string
    searchPage: string
  }
}

interface HookProps {
  config: UseSearchConfig
  size?: number
  delay?: number
  searchQueryMinLength?: number
}

const useSearch = ({
  config,
  size,
  delay = 500,
  searchQueryMinLength = 1
}: HookProps): UseSearchReturnType => {
  const pathname = usePathname()
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const isSearchPage = !!pathname?.includes(config.paths.searchPage)
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState('')

  const queryParams = queryString.stringify({
    query: searchQuery,
    ...(size ? { size } : {})
  })

  const { data, isLoading, error } = useCustomerScopedRequest<StorefrontSearchRes['content']>(
    searchQuery.length >= searchQueryMinLength ? `${config.paths.searchApi}?${queryParams}` : null,
    'content'
  )

  const [preservedData, setPreservedData] = useState<
    StorefrontSearchRes['content'] | null | undefined
  >(data)

  useEffect(() => {
    if (!isLoading) {
      setPreservedData(data)
    }
  }, [data, isLoading])

  useUpdateEffect(() => {
    setAdvancedSearchQuery('')
  }, [searchQuery])

  const clearSearch = useCallback(() => {
    setSearchQuery('')

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [])

  const goOnSearchPage = useCallback(
    (event?: KeyboardEvent<HTMLInputElement>, callback?: () => void) => {
      const value =
        (event ? (event.target as HTMLInputElement).value : inputRef.current?.value) || ''

      router.push(`${config.paths.searchPage}?q=${value}`)
      callback?.()
    },
    [config.paths.searchPage, router]
  )

  const submitAdvancedSearch = useCallback(() => {
    if (!searchQuery) {
      return
    }

    goOnSearchPage()
  }, [goOnSearchPage, searchQuery])

  const debouncedSubmitSearch = useDebouncedCallback(
    (event: KeyboardEvent<HTMLInputElement>, callback?: () => void) => {
      const value = (event.target as HTMLInputElement).value || ''
      setSearchQuery(value)

      if (!value) {
        return
      }

      if (event.key === 'Enter') {
        setAdvancedSearchQuery(value)
        goOnSearchPage(event, callback)
      } else {
        config.onSubmit?.(value)
        callback?.()
      }
    },
    delay
  )

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>, callback?: () => void) => {
    if (event.key === 'Enter') {
      debouncedSubmitSearch.flush()
      debouncedSubmitSearch(event, callback)
      return
    }

    debouncedSubmitSearch(event, callback)
  }

  return {
    onKeyDown,
    searchQuery,
    data,
    preservedData,
    isLoading,
    error,
    clearSearch,
    inputRef,
    isSearchPage,
    submitAdvancedSearch,
    advancedSearchQuery,
    setAdvancedSearchQuery,
    searchPagePath: config.paths.searchPage
  }
}

export default useSearch
